import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { useCookies } from 'react-cookie';
import usePerformanceMonitor from './hooks/usePerformanceMonitor';
import ScrollToTop from './utils/ScrollToTop';
import { ReactComponent as CloseIcon } from './assets/icone/toast/close.svg';
import 'react-toastify/dist/ReactToastify.css';
import './style/index.css';
import LoadingSpinner from './components/loading/LoadingSpinner';
import Layout from './components/utils/Layout';
import ResourceOptimizer from './components/utils/ResourceOptimizer';
import CookieConsentBanner from './components/CookieConsentBanner';

// Pages importate immediatamente (critiche per il primo caricamento)
import Home from './pages/Home';
import Login from './pages/profile/login';
import Register from './pages/profile/register';
import NotFound from './pages/error/404';
import ErrorPage from './pages/error/500';

// Lazy loading per tutte le altre pagine
const ConfirmEmail = React.lazy(() => import('./pages/profile/myTrip/ConfirmEmail'));
const Profile = React.lazy(() => import('./pages/profile/profile'));
const PublicProfile = React.lazy(() => import('./pages/profile/publicProfile'));
const CarePublicProfile = React.lazy(() => import('./pages/customerCare/CarePublicProfile'));
const AuthRoute = React.lazy(() => import('./components/AuthRoute'));
const ResetPassword = React.lazy(() => import('./pages/profile/resetPassword'));

// Ticket Sale Pages
const SellTicketPage = React.lazy(() => import('./pages/ticketSale/formSell/sellTicketPage'));
const FlighTicket = React.lazy(() => import('./pages/ticketSale/flight/flightTicket'));
const FlightTicketDetail = React.lazy(() => import('./pages/ticketSale/flight/flightTicketDetails'));
const TrainTicket = React.lazy(() => import('./pages/ticketSale/train/trainTicket'));
const TrainHistoryOffer = React.lazy(() => import('./pages/ticketSale/train/trainHistoryoffer'));
const RoomHistoryoffer = React.lazy(() => import('./pages/ticketSale/room/roomHistoryOffer'));
const FlightHistoryoffer = React.lazy(() => import('./pages/ticketSale/flight/flightOfferHistory'));
const RoomReservation = React.lazy(() => import('./pages/ticketSale/room/roomReservation'));
const TrainTicketDetail = React.lazy(() => import('./pages/ticketSale/train/trainTicketDetail'));
const RoomReservationDetail = React.lazy(() => import('./pages/ticketSale/room/roomReservationDetail'));

// Profile Pages
const TicketsAds = React.lazy(() => import('./pages/profile/ticketAds'));
const MyOffers = React.lazy(() => import('./pages/profile/myOffers/myOffers'));
const MyTrips = React.lazy(() => import('./pages/profile/myTrip/myTrips'));
const MyWallet = React.lazy(() => import('./pages/profile/wallet/MyWallet'));
const HistoryTransaction = React.lazy(() => import('./pages/profile/wallet/HistoryTransaction'));
const PaymentMethod = React.lazy(() => import('./pages/profile/wallet/PaymentMethod'));
const AddPayoutMethod = React.lazy(() => import('./pages/profile/wallet/AddPayoutMethod'));
const PayoutMethod = React.lazy(() => import('./pages/profile/wallet/PayoutMethod'));
const ProfileSettings = React.lazy(() => import('./pages/profile/profileSettings'));
const Payment = React.lazy(() => import('./pages/profile/payment/payment'));
const FavoritePage = React.lazy(() => import('./pages/profile/favoritePage'));

// Customer Care Pages
const CustomerHome = React.lazy(() => import('./pages/customerCare/customerHome'));
const Announcements = React.lazy(() => import('./pages/customerCare/announcements/announcements'));
const ToPay = React.lazy(() => import('./pages/customerCare/pay/ToPay'));
const NameChange = React.lazy(() => import('./pages/customerCare/sold/nameChange'));
const CareFlightDetails = React.lazy(() => import('./pages/customerCare/announcements/careFlightDetails'));
const CareTrainDetails = React.lazy(() => import('./pages/customerCare/announcements/careTrainDetails'));
const CareRoomDetails = React.lazy(() => import('./pages/customerCare/announcements/careRoomDetails'));
const CareFlightSold = React.lazy(() => import('./pages/customerCare/sold/careFLightSold'));
const CareTrainSold = React.lazy(() => import('./pages/customerCare/sold/careTrainSold'));
const CareRoomSold = React.lazy(() => import('./pages/customerCare/sold/careRoomSold'));
const ClosedBookings = React.lazy(() => import('./pages/customerCare/closed/closedBookings'));
const CareFlightClosed = React.lazy(() => import('./pages/customerCare/closed/CareFlightClosed'));
const CareTrainClosed = React.lazy(() => import('./pages/customerCare/closed/CareTrainClosed'));
const CareRoomClosed = React.lazy(() => import('./pages/customerCare/closed/CareRoomClosed'));
const WeldedBookings = React.lazy(() => import('./pages/customerCare/welded/weldedBookings'));
const CareFlightWelded = React.lazy(() => import('./pages/customerCare/welded/CareFlightWelded'));
const CareTrainWelded = React.lazy(() => import('./pages/customerCare/welded/CareTrainWelded'));
const CareRoomWelded = React.lazy(() => import('./pages/customerCare/welded/CareRoomWelded'));
const CancelledBookings = React.lazy(() => import('./pages/customerCare/cancelled/cancelledBookings'));
const BlockedUsersPage = React.lazy(() => import('./pages/customerCare/BlockedUsersPage'));
const AdminDashboard = React.lazy(() => import('./pages/admin/adminDashboard'));

// LoadingSpinner già importato sopra - rimuovo duplicato

// Continuiamo con lazy loading
const ReservationDetails = React.lazy(() => import('./pages/admin/ReservationDetails'));
const ReviewTicketRoom = React.lazy(() => import('./pages/reviewTicket/reviewTicketRoom'));
const ReviewTicketFlight = React.lazy(() => import('./pages/reviewTicket/reviewTicketFlight'));
const ReviewTicketTrain = React.lazy(() => import('./pages/reviewTicket/reviewTicketTrains'));
const TripDetails = React.lazy(() => import('./pages/profile/tripDetails'));
const DashboardInvestor = React.lazy(() => import('./pages/investor/DashboardInvestor'));
const ReservationReview = React.lazy(() => import('./pages/customerCare/review/reservationReview'));
const CareRoomReview = React.lazy(() => import('./pages/customerCare/review/careRoomReview'));
const CareFlightReview = React.lazy(() => import('./pages/customerCare/review/careFlightReview'));
const CareTrainReview = React.lazy(() => import('./pages/customerCare/review/careTrainReview'));
const AmministratorDashboard = React.lazy(() => import('./pages/amministrator/amministratorDashboard'));
const AmministratorFlightSold = React.lazy(() => import('./pages/amministrator/amministratorFlightSold'));
const CreateCoupon = React.lazy(() => import('./pages/admin/coupon/createCoupon'));
const AllCoupons = React.lazy(() => import('./pages/admin/coupon/allCoupon'));
const GestioneCoupon = React.lazy(() => import('./pages/admin/coupon/gestioneCoupon'));

// Layout e componenti già importati sopra

const Privacy = React.lazy(() => import('./pages/profile/Privacy'));
const Assitence = React.lazy(() => import('./pages/profile/assistence/Assistence'));
// FAQ Pages
const GeneralFaq = React.lazy(() => import('./pages/faq/GeneralFaq'));
const ProfileFaq = React.lazy(() => import('./pages/faq/ProfileFaq'));
const TicketFaq = React.lazy(() => import('./pages/faq/TicketFaq'));
const SellFaq = React.lazy(() => import('./pages/faq/SellFaq'));
const PaymentFaq = React.lazy(() => import('./pages/faq/paymentFaq'));
const BuyFaq = React.lazy(() => import('./pages/faq/BuyFaq'));
const SanctionsFaq = React.lazy(() => import('./pages/faq/SanctionsFaq'));

// Experience Pages
const Experience = React.lazy(() => import('./pages/experience/Experince'));
const AllRoomExperience = React.lazy(() => import('./pages/experience/AllRoom'));
const AllFlightExperience = React.lazy(() => import('./pages/experience/AllFlight'));
const AllTrainExperience = React.lazy(() => import('./pages/experience/AllTrain'));

// Guide Pages
const GeneralGuide = React.lazy(() => import('./pages/guide/GeneralGuide'));
const SellGuide = React.lazy(() => import('./pages/guide/SellGuide'));
const AfterDropGuide = React.lazy(() => import('./pages/guide/AfterDropGuide'));
const BuyGuide = React.lazy(() => import('./pages/guide/BuyGuide'));
// More Customer Care Pages
const CareActive = React.lazy(() => import('./pages/customerCare/active/CareActive'));
const ActiveRunning = React.lazy(() => import('./pages/customerCare/running/ActiveRunning'));
const CareRoomActiveDetails = React.lazy(() => import('./pages/customerCare/active/CareRoomActiveDetails'));
const CareFlightActiveDetails = React.lazy(() => import('./pages/customerCare/active/CareFlightActiveDetails'));
const CareTrainActiveDetails = React.lazy(() => import('./pages/customerCare/active/CareTrainActiveDetails'));
const CareRoomToPay = React.lazy(() => import('./pages/customerCare/pay/CareRoomToPay'));
const CareTrainToPay = React.lazy(() => import('./pages/customerCare/pay/CareTrainToPay'));
const CareFlightToPay = React.lazy(() => import('./pages/customerCare/pay/CareFlightToPay'));

// More Profile Pages
const PaymentsRecived = React.lazy(() => import('./pages/profile/wallet/PaymentsRecived'));
const TermsCondition = React.lazy(() => import('./pages/profile/termsCondition'));

// Static Pages
const AboutUs = React.lazy(() => import('./pages/aboutUs'));
// Cookie e utilità già importate sopra

// Remaining lazy imports
const Cookie = React.lazy(() => import('./pages/profile/Cookie'));
const AdminCreateUserForm = React.lazy(() => import('./pages/admin/AdminCreateUserForm'));
const AllUsersPage = React.lazy(() => import('./pages/customerCare/allUser/AllUserPage'));
const HelpIndex = React.lazy(() => import('./pages/help/HelpIndex'));
const HelpArticle = React.lazy(() => import('./pages/help/HelpArticle'));


function usePageTracking() {
  const location = useLocation();
  const [cookies] = useCookies(['cookieConsent']);

  useEffect(() => {
    if (cookies.cookieConsent === 'accepted' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted'
      });

      window.gtag("event", "page_view", {
        page_path: location.pathname,
      });
    }
  }, [location, cookies]);
}

const App = () => {
  const location = useLocation();
  const [cookies] = useCookies(['cookieConsent']);
  const excludedPaths = ['/customer-care', '/admin', '/amministrator', '/investor'];
  const showCookieBanner = !excludedPaths.some(path => location.pathname.startsWith(path));

  // Performance monitoring
  const { metrics, performanceScore, applyAutoOptimizations } = usePerformanceMonitor();

  // Scroll automatico in alto ad ogni cambio pagina
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  // Applica ottimizzazioni automatiche quando le performance sono basse
  useEffect(() => {
    if (performanceScore !== null && performanceScore < 70) {
      applyAutoOptimizations();
    }
  }, [performanceScore, applyAutoOptimizations]);

  useEffect(() => {
    if (cookies.cookieConsent === 'accepted') {
      const script = document.createElement('script');
      script.src = 'https://www.googletagmanager.com/gtag/js?id=G-6XG0QK250M';
      script.async = true;
      document.head.appendChild(script);

      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag() { window.dataLayer.push(arguments); }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-6XG0QK250M');
      };
    }
  }, [cookies]);

  usePageTracking();


  return (
    <>
      <ResourceOptimizer />
      <LoadingSpinner />
      {showCookieBanner && <CookieConsentBanner />}

      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={true}
        newestOnTop={true}
        closeButton={<CloseIcon />}
        closeOnClick
        pauseOnHover
        draggable
        style={{ marginTop: '70px' }}
        className="toast-container"
      />
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<AuthRoute component={Home} />} />
          <Route path="*" element={<AuthRoute component={NotFound} />} />
          <Route path="/about-us" element={<AuthRoute component={AboutUs} />} />
          <Route path="/error" element={<AuthRoute component={ErrorPage} />} />
          <Route path="/login" element={<AuthRoute component={Login} redirectTo="/profile" />} />
          <Route path="/profile" element={<AuthRoute component={Profile} private={true} rolesAllowed={['customer']} />} />
          <Route path="/auth/confirm" element={<ConfirmEmail />} />

          <Route path="/profile/public/:userId" element={<PublicProfile />} />

          <Route path="/register" element={<AuthRoute component={Register} />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/sellTicket" element={<AuthRoute component={SellTicketPage} private={true} rolesAllowed={['customer']} />} />
          <Route path="/flightTicket" element={<AuthRoute component={FlighTicket} />} />
          <Route path="/trainTicket" element={<AuthRoute component={TrainTicket} />} />
          <Route path="/roomTicket" element={<AuthRoute component={RoomReservation} />} />
          <Route path="/ticket/flight/:id" element={<FlightTicketDetail />} />
          <Route path="/ticket/train/:id" element={<TrainTicketDetail />} />
          <Route path="/ticket/room/:id" element={<RoomReservationDetail />} />
          <Route path="/experience" element={<Experience />} />
          <Route path="/experience/all-rooms" element={<AllRoomExperience />} />
          <Route path="/experience/all-flights" element={<AllFlightExperience />} />
          <Route path="/experience/all-trains" element={<AllTrainExperience />} />
          <Route path="/offer-history/trains/:id" element={<TrainHistoryOffer />} />
          <Route path="/offer-history/rooms/:id" element={<RoomHistoryoffer />} />
          <Route path="/offer-history/flights/:id" element={<FlightHistoryoffer />} />







          <Route path="/profile/ticketAds" element={<AuthRoute component={TicketsAds} private={true} rolesAllowed={['customer']} />} />
          <Route path="/profile/my-offer" element={<AuthRoute component={MyOffers} private={true} rolesAllowed={['customer']} />} />
          <Route path="/profile/my-trips" element={<AuthRoute component={MyTrips} private={true} rolesAllowed={['customer']} />} />
          <Route path="/profile/profile-settings" element={<AuthRoute component={ProfileSettings} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/my-wallet' element={<AuthRoute component={MyWallet} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/payment-menthod' element={<AuthRoute component={PaymentMethod} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/history-transaction' element={<AuthRoute component={HistoryTransaction} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/payments-reviced' element={<AuthRoute component={PaymentsRecived} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/payout-method' element={<AuthRoute component={PayoutMethod} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/add-payout-method' element={<AuthRoute component={AddPayoutMethod} private={true} rolesAllowed={['customer']} />} />
          <Route path='/profile/assistance' element={<AuthRoute component={Assitence} private={true} rolesAllowed={['customer']} />} />
          <Route path='/contact' element={<AuthRoute component={Assitence} />} />
          <Route path='/faq' element={<AuthRoute component={GeneralFaq} />} />
          <Route path='/faq-profile' element={<AuthRoute component={ProfileFaq} />} />
          <Route path='/faq-ticket' element={<AuthRoute component={TicketFaq} />} />
          <Route path='/faq-sell' element={<AuthRoute component={SellFaq} />} />
          <Route path='/faq-buy' element={<AuthRoute component={BuyFaq} />} />
          <Route path='/faq-payment' element={<AuthRoute component={PaymentFaq} />} />
          <Route path='/faq-sanctions' element={<AuthRoute component={SanctionsFaq} />} />
          <Route path='/general-guide' element={<AuthRoute component={GeneralGuide} />} />
          <Route path='/sell-guide' element={<AuthRoute component={SellGuide} />} />
          <Route path='/after-drop-guide' element={<AuthRoute component={AfterDropGuide} />} />
          <Route path='/buy-guide' element={<AuthRoute component={BuyGuide} />} />

          <Route path='/help' element={<AuthRoute component={HelpIndex} />} />
          <Route path='/help/:slug' element={<AuthRoute component={HelpArticle} />} />







          <Route path="/profile/details/:tripId" element={<TripDetails />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie" element={<Cookie />} />
          <Route path="/terms" element={<TermsCondition />} />


          <Route path="/profile/payment/:id" element={<AuthRoute component={Payment} private={true} rolesAllowed={['customer']} />} />
          <Route path="/favorite" element={<AuthRoute component={FavoritePage} private={true} rolesAllowed={['customer']} />} />
          <Route path="/review/rooms/:id" element={<AuthRoute component={ReviewTicketRoom} private={true} rolesAllowed={['customer']} />} />
          <Route path="/review/flights/:id" element={<AuthRoute component={ReviewTicketFlight} private={true} rolesAllowed={['customer']} />} />
          <Route path="/review/trains/:id" element={<AuthRoute component={ReviewTicketTrain} private={true} rolesAllowed={['customer']} />} />

        </Route>
        <Route path="/customer-care" element={<AuthRoute component={CustomerHome} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/announcements" element={<AuthRoute component={Announcements} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/active/rooms/:id" element={<AuthRoute component={CareRoomActiveDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/active/flights/:id" element={<AuthRoute component={CareFlightActiveDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/active/trains/:id" element={<AuthRoute component={CareTrainActiveDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />


        <Route path="/customer-care/active" element={<AuthRoute component={CareActive} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/running" element={<AuthRoute component={ActiveRunning} private={true} rolesAllowed={['care', 'amministrator']} />} />


        <Route path="/profile/care/public/:userId" element={<AuthRoute component={CarePublicProfile} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/profile/care/blocked-users" element={<AuthRoute component={BlockedUsersPage} private={true} rolesAllowed={['care', 'amministrator']} />} />

        <Route path="/customer-care/all-users" element={<AuthRoute component={AllUsersPage} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/name-change" element={<AuthRoute component={NameChange} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/closed-bookings" element={<AuthRoute component={ClosedBookings} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/closed/flight/:id" element={<AuthRoute component={CareFlightClosed} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/closed/train/:id" element={<AuthRoute component={CareTrainClosed} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/closed/room/:id" element={<AuthRoute component={CareRoomClosed} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/welded-bookings" element={<AuthRoute component={WeldedBookings} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/welded/flight/:id" element={<AuthRoute component={CareFlightWelded} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/welded/train/:id" element={<AuthRoute component={CareTrainWelded} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/welded/room/:id" element={<AuthRoute component={CareRoomWelded} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/cancelled-bookings" element={<AuthRoute component={CancelledBookings} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/pending/flights/:id" element={<AuthRoute component={CareFlightDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/pending/trains/:id" element={<AuthRoute component={CareTrainDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/pending/rooms/:id" element={<AuthRoute component={CareRoomDetails} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/to-pay" element={<AuthRoute component={ToPay} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/offers/rooms/:id" element={<AuthRoute component={CareRoomToPay} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/offers/trains/:id" element={<AuthRoute component={CareTrainToPay} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/offers/flights/:id" element={<AuthRoute component={CareFlightToPay} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/sold/flights/:id" element={<AuthRoute component={CareFlightSold} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/sold/trains/:id" element={<AuthRoute component={CareTrainSold} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/sold/rooms/:id" element={<AuthRoute component={CareRoomSold} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="customer-care/review" element={<AuthRoute component={ReservationReview} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/review/rooms/:id" element={<AuthRoute component={CareRoomReview} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/review/flights/:id" element={<AuthRoute component={CareFlightReview} private={true} rolesAllowed={['care', 'amministrator']} />} />
        <Route path="/customer-care/review/trains/:id" element={<AuthRoute component={CareTrainReview} private={true} rolesAllowed={['care', 'amministrator']} />} />
        
        <Route path="/admin-dashboard" element={<AuthRoute component={AdminDashboard} private={true} rolesAllowed={['admin', 'amministrator']} />} />
        <Route path="/admin/reservation-details/:type/:id" element={<AuthRoute component={ReservationDetails} private={true} rolesAllowed={['admin', 'amministrator']} />} />
        <Route path="/admin/create-coupon" element={<AuthRoute component={CreateCoupon} private={true} rolesAllowed={['admin']} />} />
        <Route path="/admin/create-user" element={<AuthRoute component={AdminCreateUserForm} private={true} rolesAllowed={['admin']} />} />
        <Route path="/admin/gestion-coupon" element={<AuthRoute component={GestioneCoupon} private={true} rolesAllowed={['admin']} />} />
        <Route path="/admin/all-coupon" element={<AuthRoute component={AllCoupons} private={true} rolesAllowed={['admin']} />} />
        <Route path="/investor-dashboard" element={<AuthRoute component={DashboardInvestor} private={true} rolesAllowed={['investor']} />} />

        <Route path="/amministrator" element={<AuthRoute component={AmministratorDashboard} private={true} rolesAllowed={['amministrator']} />} />
        <Route path="/amministrator/closed/flight/:id" element={<AuthRoute component={AmministratorFlightSold} private={true} rolesAllowed={['amministrator']} />} />
        </Routes>
      </Suspense>
    </>
  );
};

const AppWrapper = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;
